import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { galleryImages } from '../data/mock';
import { Image as ImageIcon } from 'lucide-react';

const Gallery = () => {
  const { t } = useTranslation();
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = [
    { id: 'all', labelKey: 'gallery.all' },
    { id: 'canvas', labelKey: 'gallery.canvas' },
    { id: 'aluminum', labelKey: 'gallery.aluminum' },
    { id: 'aluminum-hd', labelKey: 'gallery.aluminumHd' },
    { id: 'acrylic', labelKey: 'gallery.acrylic' },
    { id: 'banner', labelKey: 'gallery.banner' },
    { id: 'gator-board', labelKey: 'gallery.gatorBoard' },
    { id: 'framed', labelKey: 'gallery.framed' }
  ];

  const filteredImages = selectedCategory === 'all' 
    ? galleryImages 
    : galleryImages.filter(img => img.category === selectedCategory);

  return (
    <section id="gallery" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-blue-100 px-4 py-2 rounded-full mb-4">
            <ImageIcon className="w-4 h-4 text-blue-600" />
            <span className="text-sm font-semibold text-blue-700">{t('gallery.badge')}</span>
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            {t('gallery.title')} <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-600 to-blue-600">{t('gallery.titleHighlight')}</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-8">
            {t('gallery.description')}
          </p>

          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map(category => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-6 py-3 rounded-full font-semibold transition-all duration-300 transform hover:scale-105 ${
                  selectedCategory === category.id
                    ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {t(category.labelKey)}
              </button>
            ))}
          </div>
        </div>

        {/* Gallery Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredImages.map((image, index) => (
            <div 
              key={image.id}
              className="group relative overflow-hidden rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:scale-105"
              style={{ 
                animationDelay: `${index * 100}ms`,
                minHeight: '350px'
              }}
            >
              <img 
                src={image.url} 
                alt={image.title}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
              />
              
              {/* Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="absolute bottom-0 left-0 right-0 p-6 transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
                  <h3 className="text-2xl font-bold text-white mb-2">
                    {image.title}
                  </h3>
                  <span className="inline-block bg-white/20 backdrop-blur-sm px-4 py-1 rounded-full text-sm text-white font-semibold">
                    {t(categories.find(c => c.id === image.category)?.labelKey || 'gallery.all')}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Gallery;
